<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from "vue"
import { useRoute } from 'vue-router'
import { useTranslate } from '@/composables/language'
import { $bus, mqtt } from '@/common/events'
import { defaultServiceOptions } from '@/common/config'
import { $router } from '@/router'
import { dialog } from '@/common/dialog'
import { useTitle } from '@/composables/title'
import { useMenu, useSubmenu, useTopBar } from '@/composables/menu'
import { useMasterCabinet } from "@/composables/cabinet.js";
import { useAuthState } from '@/composables/auth'
import { useCabinetStatus } from "@/composables/cabinet.js";
import { canInterrupt } from "@/composables/interrupt.js";
import { useSessionTimer } from "@/composables/session";
import { useSettings } from "@/composables/settings.js";
import Breadcrumbs from '@/components/Breadcrumbs.vue'
import Pagination from '@/components/Pagination.vue'
import DropDown from '@/components/DropDown.vue'
import Table from '@/components/Table.vue'
import Toast from '@/components/Toast.vue'
import Label from '@/components/Label.vue'
import TextBox from '@/components/TextBox.vue'
import Button from '@/components/Button.vue'
import Fullscreen from '@/components/Fullscreen.vue'
import AutocompleteTextBox from '@/components/AutocompleteTextBox.vue'
import { InventoryService } from '@/services/InventoryService.js'
import { ArticleService } from '@/services/ArticleService.js'
import { CabinetService } from '@/services/CabinetService.js'
import { ConfigService } from '@/services/ConfigService.js'
import { UserService } from '@/services/UserService.js'
import { LogService } from '@/services/LogService.js'

const { t } = useTranslate();

const configService = new ConfigService();

let articleService = new ArticleService();
let cabinetService = new CabinetService();
let inventoryService = new InventoryService();
let userService = new UserService();
let logService = new LogService();

//Cache cabinet data to local storage
const authStore = useAuthState();

const articles = ref([ ]);

const cabinetId = ref(null);
const cabinetName = ref(null);
const stockTakingId = ref(null);
const stockTakingName = ref(null);
const selectedArticle = ref(null);
const selectedTrayName = ref(null);
const selectedItemId = ref(null);
const promptDeleteItemId = ref(null);

const changeTracker = ref({ });

let originalItems = null;
let unsavedChanges = null;

const wipe = ref(false);

const route = useRoute();

const isTraysLoading = ref(true);
const loadedCabinetId = ref(null);

const shouldResumeOfferSizeSelection = ref(true);

const hasTrays = computed(() => {
  return trays.value && trays.value.length;
});

async function firstInit(id) {
  cabinetId.value = id;
  try {
    console.log(cabinetId.value);
    await loadUser();
    await loadCabinet();
    await loadSettings();
    await loadGloveSizes();
    await loadGloveTrays();
    await loadLastSession();
  }
  catch (err) {
    console.error(err);
  }
  isTraysLoading.value = false;
  loadChart();
}

const { anyDoorOpen } = useCabinetStatus();
watch(anyDoorOpen, (newValue) => {
  if (newValue) {
    //console.log("EVENT DOOR OPEN");
  }
  else {
    //console.log("EVENT DOOR CLOSED");
    warningDoorOpen.value = false;
  }
});

const warningDoorOpen = ref(anyDoorOpen.value);
if (warningDoorOpen.value) {
  //console.log("INITIAL DOOR OPEN");
}

onMounted(async () => {
  useTitle(t('gloves.stock_taking_title'));
  useMenu("gloves-stock");
  useSubmenu(false);
  useTopBar(true);

  articleService = new ArticleService(defaultServiceOptions());
  cabinetService = new CabinetService(defaultServiceOptions());
  inventoryService = new InventoryService(defaultServiceOptions());
  userService = new UserService(defaultServiceOptions());
  logService = new LogService(defaultServiceOptions());

  await loadSettings();

  let piniaCabinetId = null;
  //let piniaCabinet = cabinetStore.getCabinet();
  const { masterCabinet } = useMasterCabinet();
  let piniaCabinet = masterCabinet.value;
  if (piniaCabinet) {
    piniaCabinetId = piniaCabinet.id;
  }

  let id = piniaCabinetId;
  if (id) {
    await firstInit(id);
  }
  else {
    //waitForCabinetId = true;
  }

  try {
    mqtt.ready(() => {
      mqtt.on('received', mqttReceived);
    });
  }
  catch (err) {
    dialog(err);
  }

  //Test only
  // action.value = "changing";
  // currentTrayItemUsageBeforeChange.value = "missing";
  // currentTrayItem.value = { };
});

onUnmounted(async () => {
  mqtt.removeListener('received', mqttReceived);
});

let previousWasOpen = false;

let openingTimer = null;
const isPrevOpen = ref(false);

async function mqttReceived(data) {
  //console.log("[mqtt] received", data);

  if (data.topic == "hcu/cabinet/drum/debug") {
    //console.log("debug", data.payload);
    if (data.payload.text == "OK") {
      isOpening.value = true;
      isOpen.value = false;
      isPrevOpen.value = false;

      if (openingTimer) {
        clearTimeout(openingTimer);
        openingTimer = null;
      }
    }
    /*else if (data.payload.text == "READY") {
      isOpening.value = false;
      isPrevOpen.value = false;

      isOpen.value = true; //Open

      if (openingTimer) {
        clearTimeout(openingTimer);
        openingTimer = null;
      }
    }*/
    else if (data.payload.text == "DONE") {
      isOpening.value = false;
      isPrevOpen.value = false;

      if (openingTimer) {
        clearTimeout(openingTimer);
        openingTimer = null;
      }
    }
    else if (data.payload.text == "ERR") {
      isOpening.value = false;

      if (openingTimer) {
        clearTimeout(openingTimer);
        openingTimer = null;
      }
    }
    else if (data.payload.text == "Close and continue") {
      isOpen.value = true;
      isOpening.value = false;
      isPrevOpen.value = true;

      if (openingTimer) {
        clearTimeout(openingTimer);
        openingTimer = null;
      }
    }
    else if (data.payload.text && /^[01]{10,50}$/g.test(data.payload.text)) {
      let anyDoorOpen = (data.payload.text.indexOf("1") >= 0);
      if (anyDoorOpen && isOpen.value != anyDoorOpen) {
        //Only on change
        //isEditStockQuantity.value = true;
      }
      isOpen.value = anyDoorOpen;

      //Cannot be rotating while door is open, therefore assume it is not rotating (isOpening = false)
      if (anyDoorOpen) {
        previousWasOpen = true;
        isOpening.value = false;
        isDoorClosed.value = false;

        //DOOR OPEN
        //Door just open
        //if (!applyChangeOnClose.value) {
        //  applyChange();
        //}
      }
      else if (previousWasOpen) { //Change from open to close
        previousWasOpen = false;

        isDoorClosed.value = true;

        //Resume interruptable events, e.g. smartcard removed from reader will cause log out
        canInterrupt(true);

        //Resume session timeout timer since doors are closed
        useSessionTimer(true);

        //Door just closed
        if (applyChangeOnClose.value) {
          applyChange();
        }

        //Marked as finished but waiting last open door to close
        if (finishFlag.value) {
          if (isOpen.value == false) {
            finishFlag.value = false;
            finishProcedure();
          }
        }
        else {

          //Fast changing (less user interaction)
          if (autoConfirm.value) {
            setTimeout(nextTray, 300); //Add some delay otherwise the state is not saved correctly
            //nextTray();
          }

        }
       
      }

    }
  }

  if (data.topic == "hcu/cabinet/drum/position") {
    //console.log("position", data.payload);
    if (data.payload && typeof data.payload.inputState != "undefined") {
      let before = isOpen.value;
      isOpen.value = (data.payload.inputState > 0);
      let after = isOpen.value;
      if (after == true && before != after) { //Only if changed to true
        isEditStockQuantity.value = true;
        if (openingTimer) {
          clearTimeout(openingTimer);
          openingTimer = null;
        }
      }
    }
  }

  if (data.topic == "hcu/cabinet/drum/reply") {
    //console.log("drum/reply", data.payload.message);
  }


  if (data.topic == "hcu/cabinet/drum/open/response") {
    //console.log("open/response", data.payload);
  }


}

const applyChangeOnClose = ref(false);

async function applyChange() {
  switch (action.value) {
    case "changing":
      let prevUsage = currentTrayItem.value.usage;
      let newUsage = prevUsage;
      switch (prevUsage) {
        default:
        case "empty":
          newUsage = "new";
          break;

        case "used":
          newUsage = "new";
          break;

        case "disposed":
          newUsage = "new";
          break;
      }
      if (wipe.value) {
        newUsage = "empty";
      }
      saveCurrentTray(newUsage);
      break;

    case "filling":
      saveCurrentTray("new");
      break;

    case "emptying":
      saveCurrentTray("empty");
      break;
  }
}

const lastSavedCabinetTrayId = ref(null);
const lastSavedInventoryItemId = ref(null);

async function saveCurrentTray(newUsageValue) {
  try {
    let trayId = currentTrayItem.value.id;

    let prevUsageValue = currentTrayItem.value.usage;

    console.log("saveCurrentTray", cabinetId.value, trayId, prevUsageValue, "=>", newUsageValue);

    let data = {
      usage: newUsageValue
    };

    //Update cabinet tray status
    let result = await cabinetService.updateCabinetTray(cabinetId.value, trayId, data);
    if (result.count == 0) { //Not found, then insert
      let item = { ...currentTrayItem.value };
      item.cabinetId = cabinetId.value;
      item.name = item.trayName;
      item.usage = newUsageValue;
      let inserted = await cabinetService.insertCabinetTray(cabinetId.value, item);
      console.log("insertCabinetTray", inserted);
      lastSavedCabinetTrayId.value = inserted.id;
    }
    else {
      lastSavedCabinetTrayId.value = trayId;
    }
    console.log("lastSavedCabinetTrayId", lastSavedCabinetTrayId.value);

    currentTrayItem.value.usage = newUsageValue;

    if (prevUsageValue == "used" && newUsageValue == "new") {
      try {
        console.log("dispose inventory item: used => new");
        //Dispose inventory item - make the tray free
        let item = await inventoryService.disposeInventoryItem({
          inventoryId: inventoryId.value, //Not from this batch, may be from previous batches; but this batch has issued dispose
          cabinetId: cabinetId.value, //Required
          trayName: currentTrayItem.value.trayName,
          gloveSize: currentTrayItem.value.gloveSize,
          useCase: "gloves",
          userId: user.value?.id,
        });
        console.log("dispose inventory item result", item);
        lastSavedInventoryItemId.value = item.id;
      }
      catch (err) {
        console.error("cannot dispose inventory item", err.message);
      }
    }

    //Update (insert if new) particular item from the tray
    if (newUsageValue == "new") {
      //Create inventory item
      let item = await inventoryService.insertInventoryItem({
        inventoryId: inventoryId.value,
        cabinetId: cabinetId.value, //Redundant information
        type: "item",
        status: "active",
        condition: "new",
        data: {
          trayName: currentTrayItem.value.trayName,
          gloveSize: currentTrayItem.value.gloveSize,
          useCase: "gloves",
          usage: "new",
          userId: user.value?.id,
          inserted: new Date()
        }
      });
      //lastSavedInventoryItemId.value = item.id;
      console.log("newUsageValue new inventory item result", item);
    }
    else if (newUsageValue == "empty") {
      //Dispose inventory item - make the tray free
      let item = await inventoryService.disposeInventoryItem({
        inventoryId: inventoryId.value, //Not from this batch, may be from previous batches; but this batch has issued dispose
        cabinetId: cabinetId.value, //Required
        trayName: currentTrayItem.value.trayName,
        gloveSize: currentTrayItem.value.gloveSize,
        useCase: "gloves",
        userId: user.value?.id,
      });
      console.log("dispose inventory item result", item);
      lastSavedInventoryItemId.value = item.id;
    }
    console.log("lastSavedInventoryItemId", lastSavedInventoryItemId.value);
  }
  catch (err) {
    console.error(err);
    dialog(err);
  }
}

const isEditStockQuantity = ref(false);

const alphabet = ref("ABCDEFGHIJKLMNOPQRSTUVWXYZ".replace(/[QXYW]/gi, "").split(""));

const settings = ref(null);
const gloveSizeOptions = ref(null);
const gloveStats = ref(null);

//A9-C15 => { x1: 1, y1: 9, x2: 3, y2: 15 }
function parseRange(range) {
  let m = /^([A-Z])([0-9]+)\-([A-Z])([0-9]+)$/.exec(range);
  if (!m) {
    return;
  }

  let x1 = alphabet.value.indexOf(m[1]) + 1;
  let y1 = parseInt(m[2]);

  let x2 = alphabet.value.indexOf(m[3]) + 1;
  let y2 = parseInt(m[4]);

  return { x1, y1, x2, y2 };
}

async function loadCabinet() {
  let cabinet = await cabinetService.getCabinet(cabinetId.value);
  console.log("[loadCabinet]", cabinet);
  if (cabinet) {
    cabinetName.value = cabinet.name;
  }
}

async function loadGloveSizes() {
  let data = await cabinetService.getRemoteSettings();
  if (data.gloveSizeOptions && data.gloveSizeOptions.length) {
    let sizes = data.gloveSizeOptions.filter(x => /^[A-Z][0-9]+\-[A-Z][0-9]+$/.test(x.range)).map(x => {
      return {
        value: x.value,
        range: x.range,
        ...parseRange(x.range)
      };
    });
    console.log("gloveSizeOptions", sizes);
    gloveSizeOptions.value = sizes;
  }
  settings.value = data;
}

function getGloveSize(trayName, x, y) {
  let size = gloveSizeOptions.value.find(g => (g.x1 <= x && x <= g.x2) && (g.y1 <= y && y <= g.y2));
  if (size) {
    return size.value;
  }
  return null;
}

//Find last user's session if any incomplete
async function loadLastSession() {
  let query = {
    userId: authStore.userId.value,
    cabinetId: cabinetId.value,
    type: "batch",
    status: "draft",
    skip: 0,
    take: 1,
    sort: { created: -1 }
  };

  let inventories = await inventoryService.getInventories(query);
  if (inventories.items?.length > 0) {
    lastSession.value = inventories.items[0];
  }
}

const lastSession = ref(null);

const shouldResumeLastSession = computed(() => {
  //Check if any pending session
  if (lastSession.value == null) {
    return false;
  }
  //Check if any tray to change (to fill, or to empty, or to replace gloves)
  let anyItem = trays.value?.find(x => isGloveSizeEnabled(x.gloveSize) && (!x.usage || x.usage == "empty" || x.usage == "used"));
  if (!anyItem) {
    return false;
  }
  return true;
});

async function loadGloveTrays() {
  //cabinetId.value = route.params.id;

  let positions = [ { name: "Vse", value: "_all" } ];

  let list = [ ];
  for (let j = 1; j <= 23; j++) {
    positions.push({ name: j.toString(), value: j.toString() });
    for (let i = 1; i <= 18; i++) {
      let trayName = alphabet.value[i - 1] + j.toString();
      list.push({
        id: cabinetId.value.replace(/^CA/g, "CY") + trayName,
        drumPosition: j,
        doorNumber: i,
        trayIndex: i,
        trayName: trayName,
        articleId: null,
        sku: null,
        name: null, //Article name
        quantity: null,
        unit: "pcs",
        userId: null,
        usage: "empty",
        gloveSize: getGloveSize(trayName, i, j)
      });
      //{ index: 1, sku: "123", name: "Zaščitne rokavice", quantity: 1, unit: "pcs" },
    }
  }

  let result = await cabinetService.getCabinetTrays(cabinetId.value);
  if (result && result.items && result.count > 0) {
    for (let item of list) {
      let record = result.items.find(x => x.id == item.id);
      if (record) {
        if (record.gloveSize) {
          item.gloveSize = record.gloveSize; //Override from database
        }
        item.usage = record.usage || "empty";
        item.userId = record.userId;
      }
    }
  }

  trays.value = list;
  originalItems = result.items;

  console.log("[loadGloveTrays]", list);
  console.log("total", list.length);
  console.log("free", list.filter(x => !x.usage || x.usage == "empty").length);
}

const unitList = ref([
  { name: "kos", value: "pcs" },
  { name: "m2", value: "m2" },
  { name: "g", value: "g" },
  { name: "kg", value: "kg" },
]);

const unitSelect = ref("pcs");
const quantity = ref("");

//const alphabet = ref("ABCDEFGHIJKLMNOPQRSTUVWXYZ".split(""));

const lastRow = ref(null);

const user = ref(null);

const isOpen = ref(false);
const isOpening = ref(false);
//const inProgress = ref(false);
const isDrawerVisible = ref(false);
const isStatsVisible = ref(false);

const currentDrumPosition = ref("1");
const currentDoorNumber = ref(1);
const currentDoorLetter = computed(() => {
  //return currentDoorNumber.value > 0 ? alphabet.value[currentDoorNumber.value - 1] : null;
  return null;
});

const drumPositions = ref([
  { name: "Vsi", value: "_all" },
  { name: "1", value: "1" },
  //...
]);

const drumPositionsFake = ref([
  { name: "--" },
]);

const totalCount = ref(23 * 18);
const successCount = ref(100);
const errorCount = ref(1);

const autoConfirm = ref(false); //Fast changing mode (less user interaction)
const isPageLoading = computed(() => !action.value && isTraysLoading.value && !warningDoorOpen.value);
const noTrayData = computed(() => !action.value && !isTraysLoading.value && !hasTrays.value && !warningDoorOpen.value);
const isChoosingAction = computed(() => !action.value && !isTraysLoading.value && hasTrays.value && !warningDoorOpen.value);
const mode = ref("one-phase"); //one-phase, two-phase

const currentTextClass = computed(() => {
  if (isGloveSizeFinished.value) {
    return "text-amber-500";
  }

  switch (currentTrayUsage.value) {
    default:
    case "empty":
      return "text-blue-700";

    case "used":
      return "text-red-700";
      break;

    case "missing":
      return "text-amber-500";
      break;
  }
});

const stockTaking = ref(null);

async function updateStockTaking() {
  try {
    let data = stockTaking.value;

    /*
    let record = {
      location: alphabet.value[i] + (position < 10 ? "0" : "") + position,
      position: position,
      doorNumber,
      sku: item ? item.sku : null,
      name: item ? item.name : null,
      quantity: item ? item.stock : null,
      unit: "kos",
      userStatus: !item ? "warning" : "unverified"
    };
    */

    for (let tray of data.trays) {
      let row = rows.value.find(x => x.position == tray.position && x.doorNumber == tray.doorNumber);
      if (row) {
        tray.status = row.userStatus;
        tray.items = [
          {
            id: row.id,
            name: row.name,
            sku: row.sku,
            stock: row.stock || row.quantity,
            unit: row.unit,
          }
        ];
      }
    }

    let result = await articleService.updateStockTaking(stockTakingId.value, stockTaking.value);
  }
  catch (err) {
    dialog(err.message)
  }
}

function showDrawer() {
  search.value = "";
  isDrawerVisible.value = true;
}

function hideDrawer() {
  isDrawerVisible.value = false;
  selectedItemId.value = null;
  selectedTrayName.value = null;
}

function showStatistics() {
  isStatsVisible.value = true;
}

function hideStatistics() {
  isStatsVisible.value = false;
}

async function loadUser() {
  let userId = authStore.userId.value;
  if (userId) {
    let result = await userService.getUser(userId);
    user.value = result;
    console.log("USER", result);
  }
}

async function loadSettings() {
  // autoConfirm.value = config?.usage?.fastFilling === true;

  //Note: work-around to read value immediately from the database because useSettings() load delay
  let config = await configService.getSettings();

  const { settings } = useSettings();
  autoConfirm.value = settings.value.usage?.fastFilling === true;
  canReportError.value = settings.value.usage?.canReportError === true;
  //scale.value = parseInt(settings.value.display?.scale) || 100;
  scale.value = parseInt(config.display?.scale || settings.value.display?.scale) || 100;
  console.log("[scale]", scale.value, config.display?.scale, settings.value.display?.scale);
}

const finishFlag = ref(false);

async function promptFinishProcedure() {
  let result = await dialog({
    type: "question",
    title: t('gloves.confirm_finish_title'),
    text: t('gloves.confirm_finish_text'),
    confirmButtonText: t('generic.confirm'),
    cancelButtonText: t('generic.cancel'),
  });
  
  if (result && result.confirm) {
    await finishProcedure();
  }
}

async function finishProcedure() {
  try {
    if (isOpen.value) {
      //Marked as finished but wait for last open door to close
      finishFlag.value = true;
      return;
    }

    await completeBatch(action.value);

    switch (action.value) {
      case "changing":
        try {
          let result = await logService.insertLog({
            uid: user.value?.id || user.value?.displayName || user.value?.username,
            msg: "Polnjenje in praznjenje predalov je opravljeno",
            t: "gloves",
            ts: new Date()
          });
        }
        catch (err) {
          console.error(err);
        }

        action.value = null;
        await dialog({
          type: "success",
          title: t('gloves.done_title'),
          text: t('gloves.done_gloves_replaced'),
          confirmButtonText: t('generic.ok'),
        });
        break;

      case "filling":
        try {
          let result = await logService.insertLog({
            uid: user.value?.id || user.value?.displayName || user.value?.username,
            msg: "Polnjenje predalov je opravljeno",
            t: "gloves",
            ts: new Date()
          });
        }
        catch (err) {
          console.error(err);
        }

        action.value = null;
        await dialog({
          type: "success",
          title: t('gloves.done_title'),
          text: t('gloves.done_trays_filled'),
          confirmButtonText: t('generic.ok'),
        });
        break;

      case "emptying":
        try {
          let result = await logService.insertLog({
            uid: user.value?.id || user.value?.displayName || user.value?.username,
            msg: "Praznjenje predalov je opravljeno.",
            t: "gloves",
            ts: new Date()
          });
        }
        catch (err) {
          console.error(err);
        }

        action.value = null;
        await dialog({
          type: "success",
          title: t('gloves.done_title'),
          text: t('gloves.done_trays_emptied'),
          confirmButtonText: t('generic.ok'),
        });
        break;
    }

    action.value = null;
    lastSession.value = null;
  }
  catch (err) {
    console.error(err);
    dialog(err);
  }
}

async function promptClearAllArticles() {
  let result = await dialog({
    type: "question-danger",
    title: t('gloves.confirm_clear_title'),
    text: t('gloves.confirm_clear_text'),
    confirmButtonText: t('gloves.clear_button'),
    cancelButtonText: t('generic.cancel'),
  });
  
  if (result && result.confirm) {
    for (let row of rows.value) {
      clearArticle(row);
    }

    //TODO: Mass delete stock in database
  }
}

async function promptClearArticle(row) {
  promptDeleteItemId.value = row.id;

  lastRow.value = row;
  let result = await dialog({
    type: "question-danger",
    title: t('gloves.confirm_clear_title'),
    text: t('gloves.confirm_remove_text'),
    confirmButtonText: t('gloves.clear_button'),
    cancelButtonText: t('generic.cancel'),
  });
  
  if (result && result.confirm) {
    let rowIndex = rows.value.findIndex(x => x.id == row.id);
    clearArticle(rows.value[rowIndex]);
  }

  promptDeleteItemId.value = null;
}

function clearArticle(row) {
  row.name = null;
  row.sku = null;
  row.quantity = null;
  row.unit = "pcs";
  row.article = null;
  row.articleId = null;
  row.thumbnailPath = null;
  row.thumbnailUrl = null;
}

function changeArticle(row) {
  lastRow.value = row;
  if (row.article || row.name) {
    selectedArticle.value = row.article || { name: row.name, sku: row.sku };
  }
  else {
    selectedArticle.value = null;
  }
  selectedItemId.value = row.id;
  selectedTrayName.value = row.trayName;

  //Reset search
  if (!articles.value || articles.value.length == 0) {
    onSearchRequest("");
  }

  showDrawer();
}

function selectArticle(article) {
  //let row = lastRow.value;
  let rowIndex = rows.value.findIndex(x => x.id == lastRow.value.id);
  let row = rows.value[rowIndex];

  let beforeArticleId = row.articleId;

  console.log(row);
  row.name = article.name;
  //if (isFeedMode.value) {
  //  row.quantity = null; //Empty when feeding cabinet
  //}
  //else {
  //  row.quantity = article.stock;
  //}
  row.quantity = null; //Empty
  row.unit = "kos";
  row.sku = article.sku;
  row.userStatus = "unverified";
  row.articleId = article.id;
  row.article = article;
  row.thumbnailUrl = article.thumbnailUrl;
  console.log(article)

  let afterArticleId = row.articleId;

  if (beforeArticleId != afterArticleId) {
    changeTracker.value[row.id] = { newArticleId: afterArticleId };
  }
  

  /*
  let trayIndex = stockTaking.value.trays.findIndex(x => x.position == row.position && x.doorNumber == row.doorNumber);
  if (trayIndex >= 0) {
    stockTaking.value.trays[trayIndex].items = [
      {
        id: article.id,
        name: article.name,
        sku: article.sku,
        stock: article.stock,
      }
    ];
  }
  */

  hideDrawer();
}

const trays = ref([
  //{ index: 1, sku: "123", name: "Zaščitne rokavice", quantity: 1, unit: "pcs" },
  //{ index: 2, sku: "234", name: "Alu profili", quantity: 2, unit: "pcs" },
]);

const isSearchFilter = computed({
  get() {
    if (searchFilter.value && searchFilter.value.trim().length > 0) {
      return true;
    }
    return false;
  }
});

const rows = computed(() => {
  return trays.value;
});

const rowsFiltered = computed({
  get() {
    if (searchFilter.value && searchFilter.value.trim().length > 0) {
      let text = searchFilter.value.trim();
      let uc = text.toUpperCase();
      let filterByTray = rows.value.filter(x => x.trayName && x.trayName.includes(uc));
      let filterBySKU = rows.value.filter(x => x.sku && x.sku.toUpperCase().includes(uc));
      let filterByArticle = rows.value.filter(x => x.name && x.name.toUpperCase().includes(uc));

      let unique = { };
      let filtered = [ ];
      let priorities = [ filterByTray, filterBySKU, filterByArticle ];
      for (let list of priorities) {
        for (let item of list) {
          if (!unique[item.id]) {
            filtered.push(item);
            unique[item.id] = true;
          }
          if (filtered.length > 30) {
            return filtered;
          }
        }
      }

      return filtered;
    }
    if (!currentDrumPosition.value || currentDrumPosition.value == "_all") {
      return rows.value;
    }
    return rows.value.filter(x => x.drumPosition == currentDrumPosition.value);
  }
});

const suggestions = ref([
  /*{ name: "Zaščitne rokavice" },
  { name: "Alu profili" },
  { name: "Sveder" },*/
]);

const search = ref("");

async function onSearchRequest(value) {
  try {
    let result = await articleService.getArticles({ search: value });
    articles.value = result.items.map(x => {
      return {
        id: x.id,
        name: x.title || x.name,
        sku: x.sku,
        stock: x.stock,
        thumbnailUrl: x.thumbnailUrl,
      };
    });
  }
  catch (err) {
    //dialog(err.message)
  }
}

async function onTextChanged(value) {
  onSearchRequest(value);
}

const searchFilter = ref("");

async function onFilterSearchRequest(value) {
  try {
    /*
    let result = await articleService.getArticles({ search: value });
    articles.value = result.items.map(x => {
      return {
        id: x.id,
        name: x.title || x.name,
        sku: x.sku,
        stock: x.stock,
        thumbnailUrl: x.image,
      };
    });
    */
  }
  catch (err) {
    //dialog(err.message)
  }
}

async function onFilterTextChanged(value) {
  onFilterSearchRequest(value);
}

function onQuantityChange(text, row) {
  //console.log("onQuantityChange", text, row);
  let rowIndex = rows.value.findIndex(x => x.id == row.id);
  rows.value[rowIndex].quantity = text;
  //console.log("real row", rows.value[rowIndex]);
}

function onUnitChange(selected, row) {
  //console.log("onUnitChange", selected, row);
  let unit = selected;
  // let unitItem = unitList.value.find(x => x.name == selected);
  // if (unitItem) {
  //   unit = unitItem.value;
  // }

  let rowIndex = rows.value.findIndex(x => x.id == row.id);
  rows.value[rowIndex].unit = unit;
  //console.log("real row", rows.value[rowIndex]);
}

function letter(index) {
  return alphabet.value[index];
}

const hcol = ref(7);
const hrow = ref(5);

const isHighlightRows = ref(false);

function getQuantityClass(row) {
  if (isHighlightRows.value && row.articleId) {
    //{ 'bg-green-100': row.quantityAlert == 'good', 'bg-amber-100': row.quantityAlert == 'low', 'bg-red-100': row.quantityAlert == 'empty' }
    //if (!isNaN(row.quantity)) {
      if (row.quantity <= 0) {
        return "bg-red-100";
      }
      else if (row.article?.reorderPoint && row.article.reorderPoint.minQuantity > 0 && row.quantity <= row.article.reorderPoint.minQuantity) {
        return "bg-amber-100";
      }
    //}
  }
  return null;
}

function getTrayButtonClass(i, p) {
  let opacity = "";
  if (hcol.value == i || hrow.value == p) {
    opacity = "opacity-50" + " ";
  }
  if (hcol.value == i && hrow.value == p) {
    opacity = "";
  }
  if (i * p < 25) {
    return opacity + "bg-green-500 text-white";
  }
  if (i * p < 28) {
    return opacity + "bg-amber-400 text-black";
  }
  if (i * p < 45) {
    return opacity + "bg-red-500 text-white";
  }
  //return opacity + "bg-white text-gray-900";
  return opacity + "bg-white text-white";
}

const action = ref(null);
//const action = ref("filling");
//const action = ref("emptying");

const inProgress = computed(() => !!action);

/*
const currentDrumPosition = ref(1);
const currentDoorNumber = ref(1);
const currentDoorLetter = computed(() => {
  return currentDoorNumber.value > 0 ? alphabet[currentDoorNumber.value - 1] : null;
});

const totalCount = ref(23 * 18);
const successCount = ref(100);
const errorCount = ref(1);
*/

const successPercent = computed(() => {
  return successCount.value / totalCount.value;
});

const errorPercent = computed(() => {
  return errorCount.value / totalCount.value;
});


function pie(data){
  // set size of <svg> element
  document.getElementById(data.$id).setAttribute("width", 2*data.radius);
  document.getElementById(data.$id).setAttribute("height", 2*data.radius);
  // calculate sum of values
  var sum=0;
  var radius=data.radius;
  for(var e=0; e<data.segments.length; e++){
    sum+=data.segments[e].value;
  }
  // generate proportional pie for all segments
  var startAngle=0, endAngle=0;
  for(var i=0; i<data.segments.length; i++){
    var element=data.segments[i];
    var angle=element.value * 2 * Math.PI / sum;
    endAngle+=angle;
    var svgLine=makeSVG('line',{x1: radius, y1: radius, x2: (Math.cos(endAngle)*radius+radius), y2: (Math.sin(endAngle)*radius+radius), stroke: element.color});    
    document.getElementById(data.$id).append(svgLine);
    var pathStr=
        "M "+(radius)+","+(radius)+" "+
        "L "+(Math.cos(startAngle)*radius+radius)+","+
             (Math.sin(startAngle)*radius+radius)+" "+
        "A "+(radius)+","+(radius)+
             " 0 "+(angle<Math.PI?"0":"1")+" 1 "+
             (Math.cos(endAngle)*radius+radius)+","+
             (Math.sin(endAngle)*radius+radius)+" "+
        "Z";
    var svgPath=makeSVG('path',{d: pathStr, fill: element.color});
    document.getElementById(data.$id).append(svgPath);
    startAngle+=angle;
  }
};

// SVG Maker - to draw SVG by script
function makeSVG(tag, attrs) {
  var el= document.createElementNS('http://www.w3.org/2000/svg', tag);
  for (var k in attrs)
    el.setAttribute(k, attrs[k]);
  return el;
} //SVG Maker


function loadChart() {
  var example={ //set parameters for pie chart
  $id: "pie1", //set id of <svg> containning pie
  radius: 100, //set radius of pie
  segments: [ 
    { value: 22+9+12, color: "#ef4444" }, //red
    { value: 312, color: "#3b82f6" }, //blue
    { value: 30+12+12, color: "#16a34a" }, //green
    { value: 3+1+5, color: "#f59e0b" }, //yellow
  ]
  };
  pie(example); //use this function to draw pie
}

function controllerTimeout() {
  dialog(new Error(t('gloves.open_door_not_signaled')));
  isOpening.value = false;
  isOpen.value = false;
}

async function openTray(trayName) {
  if (isOpen.value) {
    return;
  }

  if (isOpening.value) {
    return;
  }

  try {
    //Check tray name, e.g. "B01"
    let m = /^([A-Z])(\d{1,2})$/.exec(trayName);
    if (!m) {
      throw new Error(t('gloves.tray_name_invalid', { name: trayName }));
    }

    isOpening.value = true;

    //Do not allow interruptions while the task is in progress
    canInterrupt(false);

    //Avoid session timeout while the tray is open
    useSessionTimer(false);

    //Clear previous opening timeout timer
    if (openingTimer) {
      clearTimeout(openingTimer);
      openingTimer = null;
    }

    let now = new Date();
    let time = now.toISOString().split("T")[1].substr(0, 8);

    let drumPosition = parseInt(m[2]);
    let doorNumber = alphabet.value.indexOf(m[1]) + 1;
    //console.log(`${tray} => ${doorNumber} ${drumPosition}`);

    try {
      //logger.user().tray().log();
      await logService.insertLog({
        uid: user.value?.id || user.value?.displayName || user.value?.username,
        tid: trayName,
        msg: "Odpiranje predala " + trayName,
        ts: new Date()
      });
    }
    catch (err) {
      console.error(err);
    }

    //socket.emit("publish", { topic: "hcu/cabinet/drum/open", payload:{ drumPosition, doorNumber } });
    mqtt.publish({ topic: "hcu/cabinet/drum/open", payload: { drumPosition, doorNumber } });
    //let { cabinetController } = useCabinetController();
    //cabinetController.open({ drumPosition, doorNumber });

    //Start opening timeout timer in case cabinet controller does not respond
    openingTimer = setTimeout(controllerTimeout, 25000);
  }
  catch (err) {
    canInterrupt(true);
    useSessionTimer(true);
    isOpening.value = false;
    //logRecords.value.push({ text: err.message, type: "danger", direction: "outbound" });
    dialog(err);
  }
}

const isPreliminaryStage = ref(false);

const currentGloveSize = ref(null);

const currentGloveTrayName = computed(() => {
  return letter(hcol.value - 1) + hrow.value;
});

function getTrayName(col, row) {
  letter(col - 1) + row;
}

/*
async function previousTray() {
  if (!(sequence.value.length > 1)) {
    return;
  }

  let currentItem = sequence.value[sequence.value.length - 2];
  hrow.value = currentItem.drumPosition;
  hcol.value = currentItem.doorNumber;
  currentGloveSize.value = currentItem.gloveSize;

  let nextItem = null;
  switch (action.value) {
    case "changing":
      nextItem = trays.value.find(x => x != currentItem && (!x.usage || x.usage == "empty" || x.usage == "used"));
      break;

    case "filling":
      nextItem = trays.value.find(x => x != currentItem && (!x.usage || x.usage == "empty"));
      break;

    case "emptying":
      nextItem = trays.value.find(x => x != currentItem && x.usage == "used");
      break;
  }
    
  nextTrayItem.value = nextItem;
  currentTrayItem.value = currentItem;
  sequence.value.push(currentItem);

  await openCurrentTray();
}
*/

async function nextTray() {
  if (!nextTrayItem.value) {
    return;
  }

  let currentItem = nextTrayItem.value;
  hrow.value = currentItem.drumPosition;
  hcol.value = currentItem.doorNumber;
  currentGloveSize.value = currentItem.gloveSize;

  let nextItem = null;
  switch (action.value) {
    case "changing":
      nextItem = trays.value.find(x => x != currentItem && isGloveSizeEnabled(x.gloveSize) && (!x.usage || x.usage == "empty" || x.usage == "used"));
      break;

    case "filling":
      nextItem = trays.value.find(x => x != currentItem && x.gloveSize && (!x.usage || x.usage == "empty"));
      break;

    case "emptying":
      if (wipe.value) {
        nextItem = trays.value.find(x => x != currentItem && x.usage != "empty");
      }
      else {
        nextItem = trays.value.find(x => x != currentItem && x.usage == "used");
      }
      break;
  }

  nextTrayItem.value = nextItem;
  currentTrayItem.value = currentItem;
  currentTrayItemUsageBeforeChange.value = currentItem.usage;
  sequence.value.push(currentItem);

  isGloveSizeFinished.value = false;

  await openCurrentTray();
}

async function openCurrentTray() {
  await openTray(currentGloveTrayName.value);
  /*
  //TODO: Open door
  isOpening.value = true;
  setTimeout(() => {
    isOpening.value = false;
    isOpen.value = true;
    setTimeout(() => {
      isOpening.value = false;
      isOpen.value = false;
    }, 2000)
  }, 3000)
  */

  if (!applyChangeOnClose.value) {
    applyChange();
  }
}

const sequence = ref([ ]);

const canGoBack = computed(() => {
  return sequence.value && sequence.value.length > 1;
});

const nextTrayItem = ref(null);
const currentTrayItem = ref(null);
const currentTrayItemUsageBeforeChange = ref(null);

const currentTrayUsage = computed(() => {
  //return currentTrayItem.value ? (currentTrayItem.value.usage || "empty") : null;
  return currentTrayItem.value ? (currentTrayItemUsageBeforeChange.value || currentTrayItem.value.usage || "empty") : null;
});

const canGoForward = computed(() => {
  return nextTrayItem.value; //{ trayName, col, row }
});

const emptyTraysCount = computed(() => {
  return trays.value ? trays.value.filter(x => !x.usage || x.usage == "empty").length : 0;
});

const usedTraysCount = computed(() => {
  return trays.value ? trays.value.filter(x => x.usage == "used").length : 0;
});

const remainingTraysCount = computed(() => {
  switch (action.value) {
    case "changing":
      return trays.value.filter(x => x.gloveSize && (!x.usage || x.usage == "empty" || x.usage == "used")).length;

    case "filling":
      return trays.value.filter(x => x.gloveSize && (!x.usage || x.usage == "empty")).length;

    case "emptying":
      if (wipe.value) {
        return trays.value.filter(x => x.gloveSize && x.usage != "empty").length;
      }
      else {
        return trays.value.filter(x => x.gloveSize && x.usage == "used").length;
      }
  }
  return null;
});

const finished = computed(() => {
  /*
  switch (action.value) {
    case "changing":
      return remainingTraysCount.value == 0;

    case "filling":
      return emptyTraysCount.value == 0;

    case "emptying":
      return usedTraysCount.value == 0;
  }
  */
  return (!canGoForward.value || remainingTraysCount.value == 0) && !isOpening.value && isDoorClosed.value;
});

const isDoorClosed = ref();

const inventoryId = ref(null);
const inventory = ref(null);

async function createBatch(action) {
  try {
    let result = await inventoryService.insertInventory({
      cabinetId: cabinetId.value,
      userId: user.value?.id,
      type: "batch",
      data: {
        useCase: "gloves",
        action: action,
        started: new Date(),
        completed: null,
      },
      status: "draft" //in progress, not yet completed
    });

    inventoryId.value = result.id;
    inventory.value = result;
  }
  catch (err) {
    console.error(err);
  }
  return inventoryId.value;
}

async function resumeBatch() {
  try {
    inventoryId.value = lastSession.value.id;
    inventory.value = lastSession.value;
  }
  catch (err) {
    console.error(err);
  }
  return inventoryId.value;
}

async function completeBatch() {
  try {
    let result = await inventoryService.completeInventory(inventoryId.value, { status: "completed", localTime: new Date() });
  }
  catch (err) {
    console.error(err);
  }
  return inventoryId.value;
}

const enabledGloveSizes = ref(null);

function isGloveSizeEnabled(size) {
  if (enabledGloveSizes.value === null) {
    return true;
  }

  if (size && enabledGloveSizes.value instanceof Array && enabledGloveSizes.value.includes(size)) {
    return true;
  }

  return false;
}

//TODO: Rename to startReplacing(options)
async function startChanging(options) {
  hrow.value = 1;
  hcol.value = 1;

  let resume = options && options.resume ? options.resume : false;
  let preliminary = options && options.preliminary ? options.preliminary : false;

  wipe.value = false;

  sequence.value = [ ];
  finishFlag.value = false;

  let list = trays.value;
  let first = list.find(x => x.gloveSize && (!x.usage || x.usage == "empty" || x.usage == "used"));
  if (!first) {
    await dialog({
      type: "info",
      text: t('gloves.trays_all_full'),
      confirmButtonText: t('generic.ok'),
    });
    return;
  }
  
  if (preliminary) {
    if (gloveSizeOptions.value) {
      for (let item of gloveSizeOptions.value) {
        item.selected = true;
      }
    }
    isPreliminaryStage.value = true;
    return;
  }

  //If previous step was preliminary stage
  if (isPreliminaryStage.value) {
    let restoreGlovesSize = enabledGloveSizes.value;

    let enabled = [ ];
    if (gloveSizeOptions.value) {
      for (let item of gloveSizeOptions.value) {
        if (item.selected) {
          enabled.push(item.value);
        }
      }
    }
    enabledGloveSizes.value = enabled.length > 0 ? enabled : null;
    isPreliminaryStage.value = false;

    //Re-evaluate the first item due to glove size selection
    first = list.find(x => isGloveSizeEnabled(x.gloveSize) && (!x.usage || x.usage == "empty" || x.usage == "used"));
    if (!first) {
      await dialog({
        type: "info",
        text: "Vsi predali za izbrane velikosti rokavic so napolnjeni.", //in ni umazanih rokavic
        confirmButtonText: "V redu",
      });

      //This is required, otherwise "resume" button disappears and instead appears "start procedure" to start from the beginning
      enabledGloveSizes.value = restoreGlovesSize;

      return;
    }
  }

  hrow.value = first.drumPosition;
  hcol.value = first.doorNumber;
  currentGloveSize.value = first.gloveSize;
  sequence.value.push(first);
  currentTrayItem.value = first;
  currentTrayItemUsageBeforeChange.value = first.usage;
  nextTrayItem.value = list.find(x => x != first && isGloveSizeEnabled(x.gloveSize) && (!x.usage || x.usage == "empty" || x.usage == "used"));
  action.value = "changing";

  //Resume previous session or start a new one
  if (resume && lastSession.value) {
    await resumeBatch();
  }
  else {
    await createBatch("changing");
  }

  try {
    let result = await logService.insertLog({
      cid: cabinetId.value,
      uid: user.value?.id || user.value?.displayName || user.value?.username,
      msg: "Začetek polnjenja in praznenja predalov, menjava rokavic",
      t: "gloves",
      ts: new Date()
    });
  }
  catch (err) {
    console.error(err);
  }

  await openCurrentTray();

  //TODO: Write log, also write log for emptying and completed
}

async function startFilling() {
  hrow.value = 1;
  hcol.value = 1;

  wipe.value = false;

  sequence.value = [ ];
  finishFlag.value = false;

  let list = trays.value;
  let first = list.find(x => x.gloveSize && (!x.usage || x.usage == "empty"));
  if (first) {
    hrow.value = first.drumPosition;
    hcol.value = first.doorNumber;
    currentGloveSize.value = first.gloveSize;
    sequence.value.push(first);
    currentTrayItem.value = first;
    currentTrayItemUsageBeforeChange.value = first.usage;
    nextTrayItem.value = list.find(x => x != first && x.gloveSize && (!x.usage || x.usage == "empty"));
    action.value = "filling";

    await createBatch("filling");

    try {
      let result = await logService.insertLog({
        cid: cabinetId.value,
        uid: user.value?.id || user.value?.displayName || user.value?.username,
        msg: "Začetek polnjenja predalov z novimi rokavicami",
        t: "gloves",
        ts: new Date()
      });
    }
    catch (err) {
      console.error(err);
    }

    await openCurrentTray();

    //TODO: Write log, also write log for emptying and completed
  }
  else {
    await dialog({
      type: "info",
      text: t('gloves.trays_all_occupied'),
      confirmButtonText: t('generic.ok'),
    });
  }
}

async function startEmptying(options) {
  hrow.value = 1;
  hcol.value = 1;

  wipe.value = options && options.wipe;

  sequence.value = [ ];
  finishFlag.value = false;

  let list = trays.value;
  let first = wipe.value ? list.find(x => x.usage != "empty") : list.find(x => x.usage == "used");
  if (first) {
    hrow.value = first.drumPosition;
    hcol.value = first.doorNumber;
    currentGloveSize.value = first.gloveSize;
    sequence.value.push(first);
    currentTrayItem.value = first;
    currentTrayItemUsageBeforeChange.value = first.usage;
    nextTrayItem.value = wipe.value ? list.find(x => x != first && x.usage != "empty") : list.find(x => x != first && x.usage == "used");
    action.value = "emptying";

    await createBatch("emptying");

    try {
      let result = await logService.insertLog({
        cid: cabinetId.value,
        uid: user.value?.id || user.value?.displayName || user.value?.username,
        msg: "Začetek praznenja predalov",
        t: "gloves",
        ts: new Date()
      });
    }
    catch (err) {
      console.error(err);
    }

    await openCurrentTray();
  }
  else {
    await dialog({
      type: "info",
      text: wipe.value ? t('gloves.trays_all_empty') : t('gloves.no_used_gloves'),
      confirmButtonText: t('generic.ok'),
    });
  }
}

const isMoreOptionsVisible = ref(false);

function showMoreOptions() {
  isMoreOptionsVisible.value = true;
}

function hideMoreOptions() {
  isMoreOptionsVisible.value = false;
}

const canReportError = ref(false);

const isReportProblemVisible = ref(false);

function showReportProblemWindow() {
  isReportProblemVisible.value = true;
}

function hideReportProblemWindow() {
  isReportProblemVisible.value = false;
}

async function sendReport() {
  try {
    /*
    //Update cabinet tray status
    let trayId = lastSavedCabinetTrayId.value;
    let resultTray = cabinetService.updateCabinetTray(cabinetId.value, trayId, {
      usage: "new",
    });
    */

    console.log("sendReport", lastSavedCabinetTrayId.value, lastSavedInventoryItemId.value);

    //lastSavedCabinetTrayId.value;
    if (!lastSavedInventoryItemId.value) {
      throw new Error(t('gloves.tray_data_missing'));
    }

    let item = await inventoryService.missingInventoryItem({
      inventoryId: inventoryId.value, //Batch
      inventoryItemId: lastSavedInventoryItemId.value,
      userId: user.value?.id
    });

    lastSavedInventoryItemId.value = null;
    currentTrayItemUsageBeforeChange.value = "missing";

    hideReportProblemWindow();
    hideMoreOptions();
  }
  catch (err) {
    hideReportProblemWindow();
    hideMoreOptions();
    dialog(err);
  }
}

async function reportGlovesFinished() {
  try {
    console.log("reportGlovesFinished", lastSavedCabinetTrayId.value, lastSavedInventoryItemId.value);

    //Update records in database
    let item = await inventoryService.finishedInventoryItem({
      inventoryId: inventoryId.value, //Batch
      inventoryItemId: lastSavedInventoryItemId.value, //Optional, only if "used" and not "empty"
      cabinetId: cabinetId.value,
      trayName: currentGloveTrayName.value,
      gloveSize: currentGloveSize.value,
    });

    //currentTrayItemUsageBeforeChange.value = "finished";
    currentTrayItemUsageBeforeChange.value = "empty";

    //Mark as finished (affects UI labels and colors)
    isGloveSizeFinished.value = true;

    //Remove size from selection of allowed sizes
    let gloveSizes = enabledGloveSizes.value;
    let removeGloveSizeIndex = gloveSizes.indexOf(currentGloveSize.value);
    if (removeGloveSizeIndex >= 0) {
      gloveSizes.splice(removeGloveSizeIndex, 1);
    }
    enabledGloveSizes.value = gloveSizes;

    //Need to find next item to prevent taking the same size again (in case next one would be the same size)
    let currentItem = nextTrayItem.value;
    nextTrayItem.value = trays.value.find(x => x != currentItem && isGloveSizeEnabled(x.gloveSize) && (!x.usage || x.usage == "empty" || x.usage == "used"));

    hideMoreOptions();
  }
  catch (err) {
    hideMoreOptions();
    dialog(err);
  }
}

function toggleGloveSize(item) {
  if (!item) {
    return;
  }

  if (!item.selected) {
    item.selected = true;
  }
  else {
    item.selected = false;
  }
}

const isGloveSizeFinished = ref(false);

//UI scaling factor in percent
const scale = ref(100); //100% by default, could also be 125% or 150%

const scaleButtonSize = computed(() => {
  if (scale.value == 125) {
    return "lg";
  }
  if (scale.value == 150) {
    return "xl";
  }
  return "md";
});
</script>

<template>
  <div class="relative flex flex-col h-full overflow-x-hidden" :class="{ 'ui-scale-100': scale == 100, 'ui-scale-125': scale == 125, 'ui-scale-150': scale == 150 }">

    <!-- Header -->
    <!-- <div class="w-full mx-auto flex flex-shrink items-center justify-between" v-if="!isLoading"></div> -->
    <div class="flex justify-between overflow-x-hidden" v-if="!isPageLoading">

      <!-- Title -->
      <div v-if="false">
        <span class="text-lg font-bold dark:text-white" v-if="!action">Upravljanje zaloge</span>
        <span class="text-lg font-bold dark:text-white" v-if="action == 'filling'">{{ $t('gloves.fill_trays') }}</span>
        <span class="text-lg font-bold dark:text-white" v-if="action == 'emptying'">{{ $t('gloves.empty_trays') }}</span>
      </div>
      <div>
        <!-- Empty to keep the placeholder -->
      </div>

      <!-- Actions -->
      <div class="flex flex-row justify-between space-x-2">
        <Button type="secondary" :size="scaleButtonSize" v-if="!isStatsVisible && false" @click="() => isStatsVisible = true"><i class="fa fa-chart-simple"></i></Button>
        <Button type="secondary" :size="scaleButtonSize" v-if="!isDrawerVisible && false" @click="() => isDrawerVisible = true"><i class="fa fa-table"></i></Button>
        <!-- <Button type="secondary" :size="scaleButtonSize" v-if="action && canReportError && !finished" @click="showReportProblemWindow()"><i class="fa fa-bell"></i></Button> -->
        <Button type="secondary" :size="scaleButtonSize" v-if="action && !finished" @click="showMoreOptions()">{{ $t('gloves.more') }}</Button>

        <!-- <Button type="secondary" class="mx-0 sm:hidden md:hidden lg:inline-block" style="outline: none; user-select: none; box-shadow: none !important;" @click="promptClearAllArticles()">Izprazni vse</Button> -->
        <Button type="primary" :size="scaleButtonSize" class="mx-0" style="outline: none; user-select: none; box-shadow: none !important;" @click="promptFinishProcedure()" accesskey="s" v-if="action && !finished">{{ $t('gloves.finish') }}</Button>

        <!-- <Button type="secondary" style="outline: none; user-select: none; box-shadow: none !important;" @click="startEmptying({ wipe: true })" accesskey="d">Začni praznjenje omare</Button> -->
        <Button type="secondary" :size="scaleButtonSize" style="outline: none; user-select: none; box-shadow: none !important;" @click="startEmptying({ wipe: true })" v-if="isChoosingAction && mode == 'one-phase' && !finishFlag">{{ $t('gloves.empty_cabinet') }}</Button>
      </div>
    </div>

    <!-- Loading animation -->
    <div class="grid place-items-center w-full h-full overflow-x-hidden" v-if="isPageLoading">
      <Spinner size="10" style="margin: 0 auto;" />
    </div>

    <!-- Missing tray data -->
    <div class="notice-container" v-if="noTrayData">
      <p class="ui-scale-resize flex justify-center p-8">Ni podatkov o predalih!</p>
    </div>

    <!-- Missing tray data -->
    <div class="notice-container" v-if="finishFlag">
      <p class="ui-scale-resize flex justify-center p-8">Prosim zapri vrata za zaključek postopka.</p>
    </div>

    <!-- Door already open from before -->
    <div class="notice-container" v-if="warningDoorOpen">
      <p class="ui-scale-resize flex justify-center p-8">Prosim zapri odprta vrata na omari.</p>
    </div>

    <!-- Start procedure: one phase empty/refill -->
    <div class="grid place-items-center h-full" v-if="isChoosingAction && mode == 'one-phase' && !finishFlag">

      <div v-if="!isPreliminaryStage">
        <!-- Glove size and icon -->
        <div>
          <div class="grid place-items-center text-gray-200 mx-auto mb-4 ui-scale-resize">
            <div style="font-size: 72px;">
              <i class="fa fa-rotate"></i>
            </div>
          </div>
        </div>
        <div class="text-center mx-auto">
          <div class="ui-scale-resize mb-4">{{ $t('gloves.fill_and_empty') }}</div>
          <Button type="warning" :size="scaleButtonSize" :class="{ 'w-48 h-16': scale == 100, 'w-64 h-20': scale == 125, 'w-80 h-24': scale == 150 }" @click="startChanging({ resume: true, preliminary: shouldResumeOfferSizeSelection })" v-if="shouldResumeLastSession">{{ $t('gloves.continue_process') }}</Button>
          <Button type="primary" :size="scaleButtonSize" :class="{ 'w-48 h-16': scale == 100, 'w-64 h-20': scale == 125, 'w-80 h-24': scale == 150 }" @click="startChanging({ resume: false, preliminary: true })" v-else>{{ $t('gloves.start_process') }}</Button>
        </div>
      </div>
      
      <div v-if="isPreliminaryStage">
        <!-- Glove size and icon -->
        <div>
          <div class="grid place-items-center text-gray-200 mx-auto mb-4 ui-scale-resize">
            <div style="font-size: 72px;">
              <i class="fa fa-hand"></i>
            </div>
          </div>
        </div>
        <div class="text-center mx-auto">
          <div class="ui-scale-resize mb-4">{{ $t('gloves.select_glove_sizes') }}</div>
          
          <!-- <div class="w-full flex gap-2 mb-4" v-if="gloveSizeOptions">
            <div class="flex flex-row gap-2 mt-2 mb-2">
              <a href="#" v-for="(item, i) in gloveSizeOptions" :key="'dl' + i" @click.prevent="toggleGloveSize(item)" class="relative glove-size-button" :class="{ 'glove-size-highlighted': item.selected }">
                <h5 class="glove-size-button-label">{{ item.value }}</h5>
              </a>
            </div>
          </div> -->

          <div class="w-full flex gap-2 mb-4 ui-scale-resize" v-if="gloveSizeOptions">
            <div class="flex flex-row gap-2 mt-2 mb-2">
              <a href="#" v-for="(item, i) in gloveSizeOptions" :key="'dl' + i" @click.prevent="toggleGloveSize(item)" class="relative w-full check-size-button" :class="{ 'check-size-highlighted': item.selected }">
                <!-- <i class="fa fa-check text-green-500 absolute top-1 right-1"></i> -->
                <i class="fa fa-check text-green-500 pt-1.5 mr-4 ml-2" v-if="item.selected"></i>
                <i class="fa fa-xmark text-red-500 pt-1.5 mr-4 ml-2" v-if="!item.selected"></i>
                <h5 class="check-size-button-label mr-4" v-if="item.selected">{{ item.value }}</h5>
                <h5 class="check-size-button-label mr-4 text-gray-400" v-if="!item.selected">{{ item.value }}</h5>
              </a>
            </div>
          </div>

          <Button type="primary" :size="scaleButtonSize" :class="{ 'w-48 h-16': scale == 100, 'w-64 h-20': scale == 125, 'w-80 h-24': scale == 150 }" @click="startChanging({ resume: false, preliminary: false })">Nadaljuj</Button>
        </div>
      </div>

    </div>

    <!-- Start procedure: two phases, either for filling or emptiny -->
    <div class="grid place-items-center w-full h-32 mt-4" v-if="isChoosingAction && mode == 'two-phase' && !finishFlag">
      <Button type="primary" :size="scaleButtonSize" style="outline: none; user-select: none; box-shadow: none !important;" @click="startFilling()" accesskey="a">{{ $t('gloves.start_filling') }}</Button>
      <Button type="danger" :size="scaleButtonSize" style="outline: none; user-select: none; box-shadow: none !important;" @click="startEmptying()" accesskey="d">{{ $t('gloves.start_emptying') }}</Button>
    </div>
    
    <!-- Procedure in progress -->
    <div class="w-full mt-4" v-if="(action == 'filling' || action == 'emptying' || action == 'changing') && !finishFlag">
    
      <!-- Grid -->
      <div class="relative gap-2 w-full mt-8 mb-8 px-2">

          <!-- <div class="grid grid-cols-4 gap-4">
            <div class="px-6 py-4 mt-8 rounded-md border border-gray-300 bg-white text-gray-500" v-for="i in 4">
              <h5>Velikost L</h5>
              <div>3 nove</div>
              <div>3 v uporabi</div>
              <div>3 čakajo zamenjavo</div>
            </div>
          </div> -->


          <!-- Finished -->
          <div class="grid place-items-center text-center" style="min-height: 300px;" v-if="finished">
            <div>
              <div class="mt-0 text-green-500 text-md ui-scale-resize">
                <span>{{ $t('gloves.task_done') }}</span>
              </div>
              <div class="mt-2 grid place-items-center w-full ui-scale-resize">
                <span class="font-bold text-green-500" style="font-size: 40px;"><i class="fa fa-check"></i></span>
              </div>
              <div class="mt-2 grid place-items-center w-full">
                <Button type="primary" :size="scaleButtonSize" class="mx-0" style="outline: none; user-select: none; box-shadow: none !important;" @click="finishProcedure()">{{ $t('gloves.finish') }}</Button>
              </div>
            </div>
          </div>

          <!-- In progress -->
          <div class="text-center" v-else>
            <div class="mt-0 text-slate-700 text-md ui-scale-resize">
              <span v-if="!isOpening">Trenutni predal</span>
              <span v-if="isOpening">Odpiram predal...</span>
            </div>
            <div class="mt-2 grid place-items-center w-full ui-scale-resize">
              <span class="font-bold" :class="currentTextClass" style="font-size: 40px;">{{ currentGloveTrayName }}</span>
              <!-- <div class="mt-2 flex items-center">
                <span>{{ letter(hcol - 3) + hrow }} &lt; {{ letter(hcol - 2) + hrow }} &lt;</span>
                <span class="font-bold text-blue-700" style="font-size: 40px;">{{ letter(hcol - 1) + hrow }}</span>
                <span>&gt; {{ letter(hcol) + hrow }} &lt; {{ letter(hcol + 1) + hrow }}</span>
              </div> -->
            </div>

            <div class="mt-8 text-slate-700 text-md ui-scale-resize" v-if="isOpening">V teku...</div>
            <div class="mt-8 text-slate-700 text-md ui-scale-resize" v-if="!isOpening && (currentTrayUsage == 'empty' || !currentTrayUsage || currentTrayUsage == 'new') && !isGloveSizeFinished">Vstavi rokavico velikosti</div>
            <div class="mt-8 text-slate-700 text-md ui-scale-resize" v-if="!isOpening && (currentTrayUsage == 'empty' || !currentTrayUsage || currentTrayUsage == 'new' || currentTrayUsage == 'used') && isGloveSizeFinished">Zmanjkalo rokavic</div>
            <div class="mt-8 text-slate-700 text-md ui-scale-resize" v-if="!isOpening && (currentTrayUsage == 'missing') && !isGloveSizeFinished">Manjkajoča rokavica zabeležena</div>
            <div class="mt-8 text-slate-700 text-md ui-scale-resize" v-if="!isOpening && (currentTrayUsage == 'used')">Odstrani rokavico velikosti</div>
            <span class="font-bold relative ui-scale-resize" :class="currentTextClass" style="font-size: 40px;" v-if="!isOpening"><i class="fa fa-hand fa-regular"></i><i class="fa fa-ban absolute -top-1.5 -left-2.5 z-10 text-amber-700" style="font-size: 60px; opacity: 0.9;" v-if="currentTrayUsage == 'missing'"></i> {{ currentGloveSize }}</span>
            <span class="font-bold text-slate-500 ui-scale-resize" style="font-size: 40px;" v-if="isOpening"><i class="fa fa-spin fa-spinner"></i></span>


            <div class="mt-8 text-slate-700 text-md ui-scale-resize" v-if="isOpening">Počakaj, da se vrata odprejo</div>
            <div class="mt-8 text-slate-700 text-md ui-scale-resize" v-if="!isOpening && isOpen && (currentTrayUsage == 'empty' || !currentTrayUsage || currentTrayUsage == 'new') && !isGloveSizeFinished">In zapri vrata na omari</div>
            <div class="mt-8 text-slate-700 text-md ui-scale-resize" v-if="!isOpening && isOpen && (currentTrayUsage == 'missing')">Vstavi novo rokavico in zapri vrata na omari</div>
            <div class="mt-8 text-slate-700 text-md ui-scale-resize" v-if="!isOpening && isOpen && (currentTrayUsage == 'empty' || !currentTrayUsage || currentTrayUsage == 'new' || currentTrayUsage == 'used') && isGloveSizeFinished">Zapri vrata na omari za nadaljevanje</div>
            <div class="mt-8 text-slate-700 text-md ui-scale-resize" v-if="!isOpening && isOpen && (currentTrayUsage == 'used') && !isGloveSizeFinished">Vstavi novo rokavico in zapri vrata na omari</div>
            <div class="mt-8 text-slate-700 text-md ui-scale-resize" v-if="!isOpening && !isOpen && !autoConfirm">Za nadaljevanje pritisni spodnji gumb</div>

            <div class="flex flex-row gap-4 items-center justify-center ui-scale-resize" v-if="!autoConfirm">
              <!-- <Button type="secondary" class="mt-4 opacity-50" :readonly="true" v-if="isOpening || isOpen">Nazaj</Button> -->
              <!-- <Button type="secondary" class="mt-4" @click="previousTray" v-else>Nazaj</Button> -->
              <Button type="primary" :size="scaleButtonSize" class="mt-4 opacity-50" :readonly="true" v-if="isOpening || isOpen">Naprej</Button>
              <Button type="primary" :size="scaleButtonSize" class="mt-4" @click="nextTray()" v-else>Naprej</Button>
              <!-- TODO: Lock button if door is open -->
              <!-- <Button type="warning" class="mt-4" @click="showModal">Preskoči</Button> -->
            </div>

            <!-- <div class="mt-8 text-gray-500 text-sm"><i class="fa fa-circle-info text-blue-500 mr-2"></i> Za nadaljevanje zapri vrata na omari</div> -->

            <!-- Več možnosti -->
            <!-- TODO: Preferences: change fill size, e.g. shift from L to M -->
            <!-- TODO: Preferences: odpiraj samo prazne ali samo uporabljene -->
            <!-- TODO: Preferences: praznjenje -->
            <!-- TODO: Preferences: prikaž info -->
            <!-- TODO: Preferences: prikaž zemljevid -->

            <!-- Show progress bar 1/414 and percent of new or percent until completion -->
            <!-- Inventura: insert stock tracking + add session + stock tracking action: revision -->

          </div>



          <!-- Progress report -->
          <div class="fixed left-16 bottom-0 pl-4 pr-20 py-4 w-full flex flex-row justify-items-strech space-x-2 z-10 border-t bg-white" v-if="inProgress && false">
            <div class="flex flex-row justify-between w-full space-x-2 z-10">

              <div class="font-medium text-xl px-2 pt-1.5" v-if="false">
                <div class="hidden lg:block">Pozicija {{ currentDrumPosition }}</div>
                <div class="block lg:hidden">{{ currentDrumPosition }}/23</div>
                <!-- <i class="fa fa-check mx-1 text-green-400"></i> -->
                <!-- <i class="fa fa-xmark mx-1 text-red-400"></i> -->
                <!-- <i class="fa fa-exclamation-triangle mx-1 text-amber-400"></i> -->
              </div>
              <div>
              </div>

              <div class="flex flex-row gap-4 font-medium text-xl pt-1.5 ui-scale-resize">
                <div class="hidden lg:block">{{ currentDrumPosition }}/23</div>
                <div class="relative w-48 lg:w-96 bg-slate-200 rounded-sm h-2.5 mt-3 dark:bg-gray-600" v-if="true">
                  <div class="absolute left-0 top-0 bg-blue-500 h-2.5 rounded-sm" :style="'width: ' + (successPercent * 100) + '%'"></div>
                  <div class="absolute right-0 top-0 bg-red-500 h-2.5 rounded-sm" :style="'width: ' + (errorPercent * 100) + '%'"></div>
                </div>
                <div v-if="true">{{ (successPercent * 100).toFixed(0) }}%</div>
              </div>

              <div>
              </div>
              <!-- <div class="flex flex-row justify-items-strech space-x-2 z-10">
                <Button type="secondary" style="outline: none; user-select: none; box-shadow: none !important;" @click="prev" v-if="currentDrumPosition > 1">Nazaj</Button>
                <Button type="primary" style="outline: none; user-select: none; box-shadow: none !important;" @click="next" v-if="currentDrumPosition < 23">Naprej</Button>
                <Button type="success" style="outline: none; user-select: none; box-shadow: none !important;" @click="stopProcedure" v-if="currentDrumPosition == 23">Zaključi</Button>
              </div> -->
            </div>
          </div>
          

        </div>

    </div>






    <Teleport to="body">
      <div drawer-backdrop="" class="bg-gray-900/50 dark:bg-gray-900/80 fixed inset-0 z-30 transition-opacity" :class="{ 'opacity-0': !isDrawerVisible, 'opacity-100': isDrawerVisible }" v-if="isDrawerVisible" @mousedown="hideDrawer"></div>
    </Teleport>

    <Teleport to="body">
      <div drawer-backdrop="" class="bg-gray-900/50 dark:bg-gray-900/80 fixed inset-0 z-30 transition-opacity" :class="{ 'opacity-0': !isStatsVisible, 'opacity-100': isStatsVisible }" v-if="isStatsVisible" @mousedown="hideStatistics"></div>
    </Teleport>

    <!-- Drawer component -->
    <div id="drawer-example" class="fixed top-0 right-0 z-40 h-screen p-4 overflow-y-auto transition-transform bg-white sm:w-2/3 md:w-2/3 lg:w-2/3 dark:bg-gray-800" :class="{ 'translate-x-full': !isDrawerVisible }" tabindex="-1" aria-labelledby="drawer-label">
      <h5 id="drawer-label" class="inline-flex items-center mb-4 text-base font-semibold text-gray-700 dark:text-gray-400">Pregled zapolnjenosti</h5>
      <button type="button" data-drawer-hide="drawer-example" aria-controls="drawer-example" class="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 absolute top-2.5 end-2.5 flex items-center justify-center dark:hover:bg-gray-600 dark:hover:text-white" @click="hideDrawer">
        <svg class="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
          <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/>
        </svg>
        <span class="sr-only">Zapri</span>
      </button>


      <!-- Trays -->
      <div class="flex flex-col gap-2 w-full mt-2 mb-2">
        <div class="flex gap-2 w-full">
          <a href="#" class="relative w-12 aspect-square grid place-items-center px-4 py-2 rounded-lg">
            <h5 class="text-md font-medium tracking-tight text-gray-900 text-center">&nbsp;</h5>
          </a>
          <a href="#" class="relative w-12 aspect-square grid place-items-center px-4 py-2 rounded-lg">
            <h5 class="text-md font-medium tracking-tight text-gray-900 text-center">&nbsp;</h5>
          </a>
          <a href="#" class="relative w-12 aspect-square grid place-items-center px-4 py-2 rounded-lg" v-for="p in 5">
            <h5 class="text-md font-medium tracking-tight text-gray-900 text-center">
              <span :class="{ 'text-blue-500': hrow == p }">{{ p }}</span>
            </h5>
            <i class="fa fa-caret-down text-blue-500" v-if="hrow == p"></i>
          </a>
          <a href="#" class="relative w-12 aspect-square grid place-items-center px-4 py-2 rounded-lg">
            <h5 class="text-md font-medium tracking-tight text-gray-900 text-center">&nbsp;</h5>
          </a>
        </div>
        <div class="flex gap-2 w-full" v-for="i in 18">
          
          <a href="#" class="relative w-12 aspect-square grid place-items-center px-0 py-2 rounded-lg">
            <h5 class="text-md font-medium tracking-tight text-gray-900 text-center">
              <span :class="{ 'text-blue-500': hcol == i }">{{ letter(i - 1) }}</span>
              <i class="ml-2 fa fa-caret-right text-blue-500" v-if="hcol == i"></i>
            </h5>
          </a>
          <a href="#" class="relative w-12 aspect-square grid place-items-center px-0 py-2 rounded-lg">
            <h5 class="text-md font-medium tracking-tight text-gray-300 text-center">
              <span><i class="fa fa-chevron-left"></i></span>
            </h5>
          </a>
          <a href="#" v-for="p in 5" class="relative w-12 aspect-square grid place-items-center px-4 py-2 border border-gray-200 rounded-lg shadow"
            :class="getTrayButtonClass(i, p)">
            <!-- selected gets hover:bg-blue-500 hover:text-white -->
            <h5 class="text-md font-medium tracking-tight">
              <!-- <span :class="{ 'text-black': p > 15 && p != 22, 'text-gray-300': p <= 15, 'text-white': p == 22 }">L</span> -->
              <span v-if="i <= 5">L</span>
              <span v-else-if="i <= 9">M</span>
              <span v-else>S</span>
            </h5>
          </a>
          <a href="#" class="relative w-12 aspect-square grid place-items-center px-0 py-2 rounded-lg">
            <h5 class="text-md font-medium tracking-tight text-gray-300 text-center">
              <span><i class="fa fa-chevron-right"></i></span>
            </h5>
          </a>
        </div>
      </div>

    </div>


    <!-- Drawer component -->
    <div id="drawer-example" class="fixed top-0 right-0 z-40 h-screen p-4 overflow-y-auto transition-transform bg-white sm:w-1/3 md:w-1/3 lg:w-1/3 dark:bg-gray-800" :class="{ 'translate-x-full': !isStatsVisible }" tabindex="-1" aria-labelledby="drawer-label">
      <h5 id="drawer-label" class="inline-flex items-center mb-4 text-base font-semibold text-gray-700 dark:text-gray-400">Statističen pregled</h5>
      <button type="button" data-drawer-hide="drawer-example" aria-controls="drawer-example" class="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 absolute top-2.5 end-2.5 flex items-center justify-center dark:hover:bg-gray-600 dark:hover:text-white" @click="hideDrawer">
        <svg class="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
          <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/>
        </svg>
        <span class="sr-only">Zapri</span>
      </button>

      <div class="mt-2 text-gray-500">
        Na voljo še <br />- velikost L: <strong class="text-green-500">30</strong> kos<br />- velikost M: <strong class="text-green-500">12</strong> kos<br />- velikost S: <strong class="text-green-500">12</strong> kos<br /><br />
        Trenutno v uporabi <br />- velikost L: <strong class="text-amber-500">3</strong> kos<br />- velikost M: <strong class="text-amber-500">1</strong> kos<br />- velikost S: <strong class="text-amber-500">5</strong> kos<br /><br />
        Za zamenjavo <br />- velikost L: <strong class="text-red-700">22</strong> kos<br />- velikost M: <strong class="text-red-700">9</strong> kos<br />- velikost S: <strong class="text-red-700">12</strong> kos<br /><br />
        Praznih predalov <br />- skupaj <strong class="text-blue-500">312</strong> praznih
      </div>

      <div class="mt-8">
        <svg id="pie1"></svg>
      </div>

    </div>


    <Modal size="md" v-if="isReportProblemVisible" @close="hideReportProblemWindow">
      <template #header>
        <div class="flex items-center text-lg">
          {{ $t('gloves.error_label') }}
        </div>
      </template>
      <template #body>
        <div class="grid place-items-center">
          <!-- <div>
            <Label :required="true">Opis</Label>
            <TextArea v-model="techProblemText" placeholder="Vnesi sporočilo" :rows="7" class="w-full" />
          </div> -->
          <Button type="warning" @click="sendReport()" :enabled="currentTrayUsage == 'used' && lastSavedInventoryItemId">{{ $t('gloves.tray_is_empty_checkbox') }}</Button>
        </div>
      </template>
      <!-- <template #footer>
        <div class="flex justify-end">
          <Button type="primary" @click="sendReport()">Pošlji</Button>
        </div>
      </template> -->
    </Modal>


    <Modal size="lg" v-if="isMoreOptionsVisible" @close="hideMoreOptions">
      <template #header>
        <div class="flex items-center text-lg">
          {{ $t('service.more_options') }}
        </div>
      </template>
      <template #body>
        <div class="grid gap-4">
          <div v-if="canReportError">
            <Label>{{ $t('gloves.error_label') }}</Label>
            <Button type="warning" @click="sendReport()" :enabled="currentTrayUsage == 'used' && lastSavedInventoryItemId && !isGloveSizeFinished">{{ $t('gloves.tray_is_empty_checkbox') }}</Button>
          </div>
          <hr v-if="canReportError" />
          <div>
            <Label>V primeru, če zmanjka rokavic pri polnjenju</Label>
            <Button type="warning" @click="reportGlovesFinished()" :enabled="!isGloveSizeFinished">Zmanjkalo rokavic za polnjenje</Button>
            <span class="block hint mt-2 text-sm text-gray-500">Ob kliknu na zgornji gumb se postopek polnjenja nadaljuje brez rokavic velikosti <strong>{{ currentGloveSize }}</strong>. Trenutno odprti predal <strong>{{ currentGloveTrayName }}</strong> bo označen kot prazen.</span>
          </div>
        </div>
      </template>
      <template #footer>
        <div class="flex justify-end">
          <Button type="secondary" @click="hideMoreOptions()">{{ $t('generic.close') }}</Button>
        </div>
      </template>
    </Modal>




  </div>
</template>

<style scoped>
th:first-child,
td:first-child {
  @apply pl-4 pr-2 py-3;
}

th, td {
  @apply px-2 py-3;
}

.wizard th {
  @apply px-0 py-3;
}

.wizard td {
  @apply px-0 py-0;
}

td {
  border-top: 1px solid gainsboro;
}

.glove-size-button {
  @apply w-16;
  @apply flex justify-center aspect-square p-4 bg-white border border-gray-200 rounded-lg shadow hover:bg-gray-100 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700;
}

.glove-size-button-label {
  @apply pt-0 text-xl;
  @apply font-bold tracking-tight dark:text-white;
}

/*
@media only screen and (max-width: 1024px) { 
  .glove-size-button {
    @apply w-14 p-3;
  }

  .glove-size-button-label {
    @apply text-lg;
  }
}
*/

.glove-size-button.glove-size-highlighted {
  border: 2px solid rgba(26,86,219,1);
}

.glove-size-button.glove-size-selected,
.glove-size-button.glove-size-selected:hover {
  @apply bg-blue-700 text-white;
  color: white !important;
  cursor: default;
}



.check-size-button {
  @apply flex justify-center p-4 bg-white border border-gray-200 rounded-lg shadow hover:bg-gray-100 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700;
}

.check-size-button-label {
  @apply pt-0 text-xl;
  @apply font-bold tracking-tight dark:text-white;
}

/*
@media only screen and (max-width: 1024px) { 
  .glove-size-button {
    @apply w-14 p-3;
  }

  .glove-size-button-label {
    @apply text-lg;
  }
}
*/

.check-size-button.check-size-highlighted {
  border: 2px solid rgba(26,86,219,1);
}

.check-size-button.check-size-selected,
.check-size-button.check-size-selected:hover {
  @apply bg-blue-700 text-white;
  color: white !important;
  cursor: default;
}


.notice-container {
  @apply flex justify-center items-center flex-grow w-full; 
}

.ui-scale-100 {
  .impersonate-container {
    @apply mt-4;
  }

  .impersonate-label {
    @apply mb-2;
  }

  .notice-container p {
    @apply scale-100; 
  }
}

.ui-scale-125 {
  @apply overflow-hidden;

  .impersonate-container {
    @apply mt-4;
  }

  .impersonate-label {
    @apply text-lg mb-2;
  }

  .ui-scale-resize {
    @apply scale-125;
  }
}

.ui-scale-150 {
  .impersonate-container {
    @apply mt-8;
  }

  .impersonate-label {
    @apply text-xl mb-2;
  }

  .ui-scale-resize {
    @apply scale-150;
  }
}
</style>
